import React from "react";
import { Box } from "@mui/material";
import { StewartTableColumn } from "controls/global/stewart-table/StewartTable";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import DatePickerSearch from "controls/global/datepicker-search/DatePickerSearch";
import { PremiumReceivablesTransactionRecord } from "entities/UIModel/PremiumReceivablesTransactionRecord";
import { fontWeight } from "theme/defaultStyle";
import {
  formatCurrency,
  formatDate,
  toIsoDate,
} from "../../policy-management/shared/policyColumnHelpers";
import SelectSearch from "../../policy-management/shared/SelectSearch";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import { formatPeriod, toPeriodCode } from "../shared/formatters";
import InputSearch from "./InputSearch";

// Matches ColumnSettingsKey.PremiumReceivable in UISettingsController.cs, so
// hide / resize / reorder preferences persist per user.
export const PR_GRID_NAME = "PremiumReceivable";

// Width of the row-header gutter (expand chevron + selection checkbox).
// TransactionsTable pins .row-header-cell to this, and the first left-sticky
// column below must start exactly here — the gutter is itself sticky at
// left: 0, so a smaller offset lets the column scroll underneath it and a
// larger one opens a gap.
export const PR_ROW_HEADER_WIDTH = 100;

export interface BuildPrColumnsOptions {
  // Trans Type dropdown values, from /lookup/GetPIList/PremiumReceivableType.
  // Undefined until the lookup resolves, which gates the filter — see below.
  typeOptions?: OptionRenderType[];
  // (criteriaKey, value) — the key is a PremiumReceivableCriteria property
  // name, NOT necessarily the column's field (dates map to From/To pairs).
  onFiltersChange: (name: string, value: string | number | null) => void;
}

// PremiumReceivable_GetListByCriteria exposes only *_From / *_To range params
// for dates — there is no equality param — so an "on this date" header filter
// pins both ends to the same day.
function dateFilter(
  onFiltersChange: BuildPrColumnsOptions["onFiltersChange"],
  criteriaBase: string
) {
  return (date: Date | null) => {
    const value = date ? toIsoDate(date) : null;
    onFiltersChange(`${criteriaBase}From`, value);
    onFiltersChange(`${criteriaBase}To`, value);
  };
}

// Single source of truth for the grid's date columns. It builds both the column
// definitions below and the Advanced Search "Date Type" options, so the two
// can't drift apart. `field` doubles as the PremiumReceivableCriteria base name
// — the SP exposes <field>From / <field>To for each.
type PrDateColumn = {
  field: keyof PremiumReceivablesTransactionRecord;
  name: string;
  width: number;
  position: number;
  period?: boolean;
};

export const PR_DATE_COLUMNS: PrDateColumn[] = [
  { field: "TransactionDate", name: "Trans Date", width: 150, position: 3 },
  { field: "ReceivedDate", name: "Received Date", width: 150, position: 4 },
];

// Criteria bases the grid doesn't render as their own date column. They are
// still filterable, so Advanced Search offers them alongside the visible ones.
const PR_UNLISTED_DATE_FIELDS: {
  field: keyof PremiumReceivablesTransactionRecord;
  name: string;
  period?: boolean;
}[] = [
  { field: "AccountingPeriod", name: "Accounting Period", period: true },
  // Criteria base is still PolicyDate — the SP's PolicyDateFrom / PolicyDateTo
  // params take a YYYYMM varchar, not a date.
  { field: "PolicyDate", name: "Policy Year/Month", period: true },
];

export type PrDateTypeOption = OptionRenderType & { period?: boolean };

// Date Type choices for Advanced Search, in grid-column order first. The value
// is the PremiumReceivableCriteria base name — <value>From / <value>To.
export const PR_DATE_TYPE_OPTIONS: PrDateTypeOption[] = [
  ...PR_DATE_COLUMNS,
  ...PR_UNLISTED_DATE_FIELDS,
].map(({ field, name, period }) => ({
  label: name,
  value: String(field),
  period,
}));

const rightAligned = (content: React.ReactNode) => (
  <Box sx={{ display: "block", textAlign: "right" }}>{content}</Box>
);

const numberCell = (value: number | null | undefined) =>
  rightAligned(value == null ? "" : value.toLocaleString());

const currencyCell = (value: number | null | undefined) =>
  rightAligned(formatCurrency(value));

export function buildPrColumns(
  opts: BuildPrColumnsOptions
): StewartTableColumn<PremiumReceivablesTransactionRecord>[] {
  const { typeOptions, onFiltersChange } = opts;

  return [
    {
      field: "AgentID",
      name: "Agent ID",
      width: 150,
      minWidth: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 1,
      // Pin to the left, just past the row-header (checkbox/expand) column.
      sticky: true,
      left: PR_ROW_HEADER_WIDTH,
      actionComponent: () => (
        <InputSearch name="AgentID" onChange={onFiltersChange} />
      ),
      valueGetter: (row) => (
        <Box component="span" sx={{ fontWeight: fontWeight.bold1 }}>
          {row.AgentID || ""}
        </Box>
      ),
    },
    {
      field: "AccountingPeriod",
      name: "Accounting Period",
      // Header labels render uppercase at 14px, so widths must clear the
      // uppercase label plus ~25px of cell chrome (padding + sort icon).
      width: 200,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 2,
      actionComponent: () => (
        <InputSearch
          name="AccountingPeriod"
          period
          placeholder="MM/YYYY"
          onChange={(name, value) =>
            onFiltersChange(name, toPeriodCode(value as string))
          }
        />
      ),
      valueGetter: (row) => (
        <TooltipCell title={formatPeriod(row.AccountingPeriod)} />
      ),
    },
    {
      field: "TransactionDate",
      name: "Trans Date",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 3,
      actionComponent: () => (
        <DatePickerSearch
          header
          size="small"
          handleDateChange={dateFilter(onFiltersChange, "TransactionDate")}
        />
      ),
      valueGetter: (row) => <TooltipCell title={formatDate(row.TransactionDate)} />,
    },
    {
      field: "ReceivedDate",
      name: "Received Date",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 4,
      actionComponent: () => (
        <DatePickerSearch
          header
          size="small"
          handleDateChange={dateFilter(onFiltersChange, "ReceivedDate")}
        />
      ),
      valueGetter: (row) => <TooltipCell title={formatDate(row.ReceivedDate)} />,
    },
    {
      field: "PolicyYearMonth",
      name: "Policy Month/Year",
      width: 190,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 5,
      actionComponent: () => (
        <InputSearch
          name="PolicyYearMonth"
          period
          placeholder="MM/YYYY"
          onChange={(name, value) =>
            onFiltersChange(name, toPeriodCode(value as string))
          }
        />
      ),
      valueGetter: (row) => (
        <TooltipCell title={formatPeriod(row.PolicyYearMonth)} />
      ),
    },
    {
      field: "PremiumReceivableTypeCode",
      name: "Trans Type",
      width: 140,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 6,
      // Rendered only once the lookup has returned rows — note the length test,
      // not a truthiness test: the hook starts at []. SelectSearch stores its
      // selection as an index into `options` in uncontrolled state, so mounting
      // it empty would let a pre-load pick resolve to the wrong code, with no
      // way for the parent to reset it.
      actionComponent: typeOptions?.length
        ? () => (
            <SelectSearch<string>
              name="PremiumReceivableTypeCode"
              options={typeOptions}
              onChange={onFiltersChange}
            />
          )
        : undefined,
      // Code in the cell, full type name in the tooltip.
      valueGetter: (row) => (
        <TooltipCell title={row.PremiumReceivableTypeName || ""}/>
      ),
    },
    {
      field: "PolicyCount",
      name: "# Policies",
      width: 120,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 7,
      // No actionComponent: the counts are not searchable. The filter row still
      // renders an empty cell for this column, so nothing shifts.
      valueGetter: (row) => numberCell(row.PolicyCount),
    },
    {
      field: "ProductCount",
      name: "# Products",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 8,
      // Not searchable, same as the other counts. The API does not return
      // ProductCount yet, so numberCell renders an empty cell until it does.
      valueGetter: (row) => numberCell(row.ProductCount),
    },
    {
      field: "SIPolicyCount",
      name: "# S.I. Policies",
      width: 140,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 9,
      // Not searchable, same as # Policies above.
      valueGetter: (row) => numberCell(row.SIPolicyCount),
    },
    {
      field: "LiabilityAmount",
      name: "Liability",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 10,
      actionComponent: () => (
        <InputSearch name="LiabilityAmount" numeric onChange={onFiltersChange} />
      ),
      valueGetter: (row) => currencyCell(row.LiabilityAmount),
    },
    {
      field: "SILiabilityAmount",
      name: "S.I. Liability",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 11,
      actionComponent: () => (
        <InputSearch name="SILiabilityAmount" numeric onChange={onFiltersChange} />
      ),
      valueGetter: (row) => currencyCell(row.SILiabilityAmount),
    },
    {
      field: "ActualFee",
      name: "Gross Premium",
      width: 200,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 12,
      actionComponent: () => (
        <InputSearch name="ActualFee" numeric onChange={onFiltersChange} />
      ),
      valueGetter: (row) => currencyCell(row.ActualFee),
    },
    {
      // Amount used to calculate remittance — Risk Amount in Risk Rate states,
      // Premium Amount otherwise. Driven by state configuration.
      field: "ActualRiskRate",
      name: "Remit Basis",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 13,
      actionComponent: () => (
        <InputSearch name="ActualRiskRate" numeric onChange={onFiltersChange} />
      ),
      valueGetter: (row) => currencyCell(row.ActualRiskRate),
    },
    {
      field: "ActualRemittance",
      name: "UW Retention",
      width: 180,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 14,
      actionComponent: () => (
        <InputSearch name="ActualRemittance" numeric onChange={onFiltersChange} />
      ),
      valueGetter: (row) => currencyCell(row.ActualRemittance),
    },
  ];
}
